import {DomainStore} from 'shared/store';
import { makeAutoObservable } from 'mobx';
import filestack from 'filestack-js';
import {successAlert} from 'shared/tools';
import JobSite from 'main/stores/recruiting/JobSite';
import InvalidIndeedLocation from 'main/stores/recruiting/InvalidIndeedLocation';
import _ from 'lodash';
import {endpoints, types} from 'main/tools';
import {t, locale, filestackUploadPolicy} from 'shared/core';

class JobSiteState {
  store = new DomainStore();

  jobSite;
  invalidLocations = [];
  errors = {};
  deactivateIndeedModalOpen = false;
  editingJobSite = new JobSite();

  constructor() {
    makeAutoObservable(this, {
      store: false
    });
  }

  async load() {
    await this.store._compose([
      endpoints.RECRUITING.JOB_SITE.SETTINGS
    ]);

    this.jobSite = new JobSite(this.store._getSingle(types.RECRUITING.JOB_SITE.SETTING));

    if (this.jobSite.indeedEnabled) {
      await this.loadInvalidIndeedLocations();
    }
  }

  async loadInvalidIndeedLocations() {
    await this.store._compose([
      endpoints.RECRUITING.JOB_SITE.INVALID_INDEED_LOCATIONS
    ]);

    this.invalidLocations = this.store._getAll(types.RECRUITING.JOB_SITE.INVALID_INDEED_LOCATION, InvalidIndeedLocation);
  }

  async saveJobSite() {
    const {errors, model} = await this.store.patch(
      endpoints.RECRUITING.JOB_SITE.SETTINGS,
      types.RECRUITING.JOB_SITE.SETTING,
      this.jobSite
    );

    if (model) {
      this.jobSite.merge(model);
    }
    this.errors = errors;
  }

  async saveForm() {
    await this.saveJobSite();

    if (_.isEmpty(this.errors)) {
      successAlert(t(`recruiting.alerts.Job site successfully updated`));
    }
  }

  openEditIndeedModal() {
    this.editingJobSite.merge(this.jobSite);
  }

  async saveIndeedForm() {
    this.jobSite.merge({
      ...this.editingJobSite,
      indeedEnabled: true,
      indeedApplyEnabled: true,
      indeedDispositionSyncEnabled: true
    });
    await this.saveJobSite();

    if (_.isEmpty(this.errors)) {
      await this.loadInvalidIndeedLocations();
      this.closeEditIndeedModal();
      successAlert(t(`recruiting.alerts.Indeed settings successfully updated`));
    }
  }

  clearSocialSharingImage() {
    this.jobSite.updateSocialSharingImage(null);
  }

  openDeactivateIndeedModal() {
    this.deactivateIndeedModalOpen = true;
  }

  closeDeactivateIndeedModal() {
    this.deactivateIndeedModalOpen = false;
  }

  async deactivateIndeedIntegration() {
    this.jobSite.merge({
      indeedEnabled: false,
      indeedApplyEnabled: false,
      indeedDispositionSyncEnabled: false,
      indeedIntegrationStatus: null
    });

    await this.saveJobSite();
    successAlert(t(`recruiting.alerts.Indeed settings successfully updated`));
    this.closeDeactivateIndeedModal();
  }

  get indeedFormSaveCaption() {
    if (this.jobSite.indeedEnabled) {
      return 'recruiting.job_site.Save';
    } else {
      return 'recruiting.job_site.Activate';
    }
  }

  get widgetCodeSnippet() {
    return `<script src='https://secure.collage.co/static/js/embed.js'></script>
      <link rel='stylesheet' href='https://secure.collage.co/static/style/embed.css'>
      <script>
        document.addEventListener('DOMContentLoaded', () => collageEmbed('${this.jobSite.urlSlug}', '${locale}'));
      </script>
      <div id='collage_embed_container'/>`;
  }

  get showIndeedStatusInfo() {
    return this.jobSite.indeedEnabled && this.jobSite.indeedIntegrationStatus;
  }

  openFileStack() {
    const client = filestack.init(import.meta.env.VITE_FILESTACK_KEY, filestackUploadPolicy);
    client.pick({
      accept: ['.apng', '.png', '.avif', '.gif', '.jpg', '.jpeg', '.jfif', '.pjpeg', '.pjp', '.webp'],
      storeTo: {
        location: 's3',
        path: '/avatars/',
      }
    }).then(response => {
      const file = _.head(response.filesUploaded);
      client.metadata(file.handle, {height: true, width: true}).then(metadata => {
        this.jobSite.updateSocialSharingImage({
          fileName: file.filename,
          fileSize: file.size,
          contentType: file.mimetype,
          key: file.key,
          label: file.url,
          width: metadata.width,
          height: metadata.height
        });
      });
    });
  }
}

export default JobSiteState;
